/**
 * Cryptographically secure, unbiased random helpers.
 * FAIRNESS INVARIANT: every winner is chosen with `secureRandomInt` — never Math.random.
 */

const RANGE = 2 ** 32;

/**
 * Uniform integer in [0, maxExclusive) using crypto.getRandomValues with
 * rejection sampling (no modulo bias).
 */
export function secureRandomInt(maxExclusive, cryptoImpl = globalThis.crypto) {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > RANGE) {
    throw new RangeError(`secureRandomInt: invalid bound ${maxExclusive}`);
  }
  const limit = RANGE - (RANGE % maxExclusive);
  const buffer = new Uint32Array(1);
  for (;;) {
    cryptoImpl.getRandomValues(buffer);
    if (buffer[0] < limit) return buffer[0] % maxExclusive;
  }
}

/** Uniform float in [0, 1) — for cosmetic choices (animation offsets). */
export function secureRandom(cryptoImpl = globalThis.crypto) {
  const buffer = new Uint32Array(1);
  cryptoImpl.getRandomValues(buffer);
  return buffer[0] / RANGE;
}

export function pickRandom(list, cryptoImpl) {
  if (!list.length) throw new RangeError('pickRandom: empty list');
  return list[secureRandomInt(list.length, cryptoImpl)];
}

/** Fisher–Yates shuffle (returns a new array). */
export function shuffle(list, cryptoImpl) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1, cryptoImpl);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** `count` distinct items chosen uniformly (partial Fisher–Yates). */
export function sample(list, count, cryptoImpl) {
  const n = Math.min(count, list.length);
  const pool = [...list];
  for (let i = 0; i < n; i++) {
    const j = i + secureRandomInt(pool.length - i, cryptoImpl);
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}

/** Short random ID that works in insecure contexts too (unlike randomUUID). */
export function uid() {
  const bytes = new Uint8Array(9);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 14);
}
